/**
 * Hooks para el módulo de Notificaciones (NOT-01).
 * Endpoints: GET /notifications/, GET /notifications/unread-count/,
 *            POST /notifications/{id}/read/, POST /notifications/read-all/
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/lib/apiClient'

// ─── Types ────────────────────────────────────────────────────────────────────

export type NotificationEventType =
  | 'document_accepted'
  | 'document_rejected'
  | 'document_error'
  | 'recurring_generated'
  | 'certificate_expiring'
  | 'payment_confirmed'
  | 'system_error'

export interface Notification {
  id: string
  title: string
  message: string
  event_type: NotificationEventType
  is_read: boolean
  metadata: Record<string, unknown>
  action_url?: string
  created_at: string
}

export interface NotificationListResponse {
  count: number
  notifications: Notification[]
}

// ─── Hooks ────────────────────────────────────────────────────────────────────

export function useUnreadCount() {
  return useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: async () => {
      const res = await apiClient.get<{ count: number }>('/notifications/unread-count/')
      return res.data.count
    },
    refetchInterval: 300_000,  // fallback poll every 5 min (WS handles real-time)
    staleTime: 30_000,
  })
}

export function useNotifications(params?: { unread?: boolean }) {
  return useQuery({
    queryKey: ['notifications', 'list', params],
    queryFn: async () => {
      const res = await apiClient.get<NotificationListResponse>('/notifications/', { params })
      return res.data
    },
  })
}

export function useMarkRead() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      apiClient.post(`/notifications/${id}/read/`).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] })
    },
  })
}

export function useMarkAllRead() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () =>
      apiClient.post('/notifications/read-all/').then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] })
    },
  })
}

// ─── Preferences (Fase B) ─────────────────────────────────────────────────────

export type NotificationChannel = 'in_app' | 'email'
export const INDIVIDUAL_CHANNELS: NotificationChannel[] = ['in_app', 'email']

export interface NotificationPreference {
  event_type: string
  description: string
  module: string
  action: string
  default_channels: NotificationChannel[]
  forced_channels: NotificationChannel[]
  channels: NotificationChannel[]
  is_override: boolean
}

export function useNotificationPreferences() {
  return useQuery({
    queryKey: ['notifications', 'preferences'],
    queryFn: async () => {
      const res = await apiClient.get<{ preferences: NotificationPreference[] }>(
        '/notifications/preferences/',
      )
      return res.data.preferences
    },
    staleTime: 60_000,
  })
}

export function useUpdateNotificationPreferences() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (preferences: { event_type: string; channels: NotificationChannel[] }[]) =>
      apiClient.put('/notifications/preferences/', { preferences }).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications', 'preferences'] })
    },
  })
}

// ─── Tenant-level notification settings (Fase C) ──────────────────────────────

export interface TenantNotificationSetting {
  event_type: string
  description: string
  module: string
  action: string
  registry_default: NotificationChannel[]
  channels: NotificationChannel[]
  forced_channels: NotificationChannel[]
  external_route_ids: string[]
  is_override: boolean
}

export function useTenantNotificationSettings() {
  return useQuery({
    queryKey: ['notifications', 'tenant-settings'],
    queryFn: async () => {
      const res = await apiClient.get<{ settings: TenantNotificationSetting[] }>(
        '/notifications/tenant-settings/',
      )
      return res.data.settings
    },
    staleTime: 60_000,
  })
}

export function useUpdateTenantNotificationSettings() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (settings: {
      event_type: string
      channels: NotificationChannel[]
      forced_channels: NotificationChannel[]
      external_route_ids: string[]
    }[]) =>
      apiClient.put('/notifications/tenant-settings/', { settings }).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications', 'tenant-settings'] })
      qc.invalidateQueries({ queryKey: ['notifications', 'preferences'] })
    },
  })
}

// ─── External channels (Fase D v2 — multi-route) ─────────────────────────────

export type ExternalChannelType = 'slack' | 'teams' | 'google_chat'

export interface ExternalChannel {
  id: string
  channel_type: ExternalChannelType
  name: string
  url: string
  is_active: boolean
  updated_at: string
}

export function useExternalChannels() {
  return useQuery({
    queryKey: ['notifications', 'external-channels'],
    queryFn: async () => {
      const res = await apiClient.get<{ channels: ExternalChannel[] }>(
        '/notifications/external-channels/',
      )
      return res.data.channels
    },
    staleTime: 60_000,
  })
}

export function useCreateExternalChannel() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: { channel_type: ExternalChannelType; name: string; url: string; is_active: boolean }) =>
      apiClient.post('/notifications/external-channels/', payload).then(r => r.data as ExternalChannel),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications', 'external-channels'] })
    },
  })
}

export function useUpdateExternalChannel() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...patch }: { id: string; name?: string; url?: string; is_active?: boolean }) =>
      apiClient.patch(`/notifications/external-channels/${id}/`, patch).then(r => r.data as ExternalChannel),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications', 'external-channels'] })
    },
  })
}

export function useDeleteExternalChannel() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      apiClient.delete(`/notifications/external-channels/${id}/`).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications', 'external-channels'] })
      qc.invalidateQueries({ queryKey: ['notifications', 'tenant-settings'] })
    },
  })
}

export function useTestExternalChannel() {
  return useMutation({
    mutationFn: (id: string) =>
      apiClient.post(`/notifications/external-channels/${id}/test/`).then(r => r.data as { ok: boolean }),
  })
}
